// apps/admin/src/lib/queryClient.ts
//
// Phase 8.1 Wave 2 Task 08 — admin-portal TanStack Query client.
// Single source of QueryClient config so every page consumer shares
// the same caching window + retry policy.
//
// Defaults (per task plan):
//   • staleTime 30s — list views feel snappy on revisit without
//     hammering the API on every focus event.
//   • gcTime 5min — keep evicted query data around long enough that
//     back-button navigation doesn't always refetch.
//   • refetchOnWindowFocus true — admins switch tabs constantly; we
//     want fresh data on return. (Per-query overrides exist for
//     unstable list pages — see usage sites.)
//   • retry 2 — limited retries for transient 5xx; ApiError(0) network
//     errors get one retry then surface to the user.
//
// vg-binding: PLAN:task-08
// vg-binding: P8.D-160
// vg-binding: P8.D-174

import { QueryClient } from '@tanstack/react-query';

import { ApiError } from './apiClient.js';

/**
 * Skip retry for non-retryable error classes. Network errors (status 0)
 * get one retry; everything else respects the default retry budget.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError) {
    // Never retry client errors (4xx) — the request itself is wrong.
    if (error.status >= 400 && error.status < 500) return false;
    // Network or 5xx: retry up to 2 times (3 total attempts).
    return failureCount < 2;
  }
  return failureCount < 2;
}

/**
 * Canonical admin-portal QueryClient. Exported as a single instance so
 * `<QueryClientProvider client={queryClient}>` consumers all share one
 * cache.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: shouldRetry,
    },
    mutations: {
      // Mutations never auto-retry — callers explicitly retry via UI.
      retry: false,
    },
  },
});

export default queryClient;
